/**
 * Stale chunk recovery
 *
 * After a deploy the JS chunk hashes change and the old files are gone from
 * S3. A browser still holding the previous index.html asks for a chunk that no
 * longer exists; CloudFront answers the SPA fallback (index.html, text/html),
 * so the import fails with a MIME-type or "failed to fetch dynamically
 * imported module" error.
 *
 * Reloading once fetches the fresh index.html and fixes it. The sessionStorage
 * flag keeps that reload to a single attempt: if the chunk is genuinely
 * missing rather than stale, the page must show an error instead of looping.
 */

const RELOAD_KEY = 'besafe_chunk_reload';

const STALE_CHUNK_MESSAGES = [
  'Failed to fetch dynamically imported module',
  'Importing a module script failed',
  'error loading dynamically imported module',
  'Expected a JavaScript-or-Wasm module script',
];

/** Whether an error looks like a chunk left behind by a previous deploy. */
export function isStaleChunkError(error: unknown): boolean {
  const msg =
    error instanceof Error ? error.message : String((error as { message?: string })?.message ?? error ?? '');
  return STALE_CHUNK_MESSAGES.some((m) => msg.includes(m));
}

/**
 * Reload once when the error is a stale chunk.
 *
 * @returns true if a reload was triggered, so the caller can stop rendering
 *   its error UI: the page is about to be replaced.
 */
export function reloadOnceOnStaleChunk(error: unknown): boolean {
  if (!isStaleChunkError(error)) return false;
  if (sessionStorage.getItem(RELOAD_KEY)) return false;

  sessionStorage.setItem(RELOAD_KEY, '1');
  window.location.reload();
  return true;
}
